import React, { useEffect, useState } from "react";
import { useParams, Outlet, useNavigate } from "react-router-dom";
import { api, setTenantSlug } from "../lib/api";
import { setSessionSlug } from "../lib/session";

/**
 * TenantScope wraps every /:slug/* route.
 * - Sets active slug on api.js + session.js (synchronously, before children render)
 * - Validates the tenant exists (404 → redirect to /)
 * - Exposes tenant via outlet context
 */
const TenantScope = () => {
    const { slug } = useParams();
    const nav = useNavigate();
    const [tenant, setTenant] = useState(null);
    const [notFound, setNotFound] = useState(false);

    // Configure synchronously so child renders + first API calls use the correct slug
    setTenantSlug(slug);
    setSessionSlug(slug);

    useEffect(() => {
        let cancelled = false;
        setTenantSlug(slug);
        setSessionSlug(slug);
        setNotFound(false);
        api.get(`/t/${slug}/info`)
            .then((r) => {
                if (cancelled) return;
                setTenant(r.data);
                // Café is hard-locked (unpaid). Redirect to the friendly
                // customer-facing unavailable page — unless the visitor is
                // on the admin/login/subscription routes, where the owner
                // needs to still be able to log in and pay to unlock.
                if (r.data?.is_locked) {
                    const path = window.location.pathname;
                    const ownerAllowed =
                        path.includes(`/${slug}/admin`) ||
                        path.includes(`/${slug}/unavailable`) ||
                        path.includes(`/${slug}/staff`);
                    if (!ownerAllowed) nav(`/${slug}/unavailable`, { replace: true });
                }
            })
            .catch((e) => {
                if (cancelled) return;
                if (e?.response?.status === 404) setNotFound(true);
                else setTenant({ name: slug, currency: "AUD" });
            });
        return () => {
            cancelled = true;
        };
    }, [slug, nav]);

    if (notFound) {
        return (
            <div className="min-h-screen bg-[#F9F6F0] flex flex-col items-center justify-center p-8 text-center">
                <div className="font-display font-black text-4xl text-[#2C1A12]">
                    404 · No café here
                </div>
                <p className="mt-3 text-[#8C7A72] max-w-md">
                    We couldn't find a café at{" "}
                    <span className="font-mono font-bold">/{slug}</span>. Check the URL or explore
                    Coffeein7 below.
                </p>
                <button
                    data-testid="notfound-home-btn"
                    onClick={() => nav("/")}
                    className="mt-8 px-8 py-4 rounded-full bg-[#FF4F18] text-white font-display font-bold shadow-[0_12px_32px_rgba(255,79,24,0.32)] active:scale-95"
                >
                    Back to Coffeein7
                </button>
            </div>
        );
    }

    return <Outlet context={{ tenant, slug }} />;
};

export default TenantScope;
